import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Tabs from '../Tabs';
import TopHolders from './TopHolders';
import HoldingsMovementReports from './HoldingsMovementReports';
import HolderDetails from './HolderDetails';
import RegisterListing from './RegisterListing';
import ShareRegistrySettings from './ShareRegistrySettings';
import { uppercaseFirstLetter } from '../../util/util';

interface IProps {
    ticker: string;
    exchange: string;
    combinedTicker: string;
    tab?: string;
}

const HoldingsMovementTabName: string = 'Holdings Movement';
const SearchTabName: string = 'Search';
// temporary, to compare against the grouped Search tab — remove once they agree
const TopShareholdersTabName: string = 'Top Shareholders';
const SettingsTabName: string = 'Settings';
const tabNames: string[] = [SearchTabName, TopShareholdersTabName, HoldingsMovementTabName, SettingsTabName];

const ShareRegistryPage: React.FC<IProps> = (props: IProps) => {

    // Only honour a known sub-tab from the URL; anything else defers to Tabs,
    // which activates the first tab (Search).
    const initialTab = uppercaseFirstLetter(props.tab) || '';
    const [activeTab, setActiveTab] = useState<string>(tabNames.includes(initialTab) ? initialTab : '');

    // in-app navigation (e.g. TopHolders row click pushes /Search?holderId=...) must switch the tab.
    // props.tab can be stale (sub-tab clicks only replaceState), so read the live location instead.
    const location = useLocation();
    useEffect(() => {
        const urlTab = uppercaseFirstLetter(decodeURIComponent(location.pathname.split('/')[4] || '')) || '';
        if (tabNames.includes(urlTab)) setActiveTab(urlTab);
    }, [location]);

    // Persist the active sub-tab as the 4th path segment
    // (/client-stocks/:ticker/Share Registry/:subtab) so refresh/back restores it.
    const setActiveTabState = (tab: string) => {
        setActiveTab(tab);
        const tokens = window.location.pathname.split('/').slice(0, 4);
        tokens.push(tab);
        // keep the query string (?holderId=...) unless the user is switching to a different tab
        const query = tab === activeTab ? window.location.search : '';
        window.history.replaceState({}, document.title, tokens.join('/') + query);
    }

    // holder selection travels in the query string (?holderId=... canonical, or ?name=...&holding=... from a list click)
    const search = new URLSearchParams(window.location.search);
    const holderQuery = (search.has('holderId') || search.has('holderNumber') || search.has('name')) ? search : null;

    return <Tabs onTabChanged={setActiveTabState} activeTab={activeTab}>

        <div data-label={SearchTabName}>
            {holderQuery
                ? <HolderDetails key={holderQuery.toString()}
                    ticker={props.ticker} exchange={props.exchange} combinedTicker={props.combinedTicker}
                    holderId={holderQuery.get('holderId') || undefined}
                    holderNumber={holderQuery.get('holderNumber') || undefined}
                    name={holderQuery.get('name') || undefined}
                    holding={holderQuery.get('holding') || undefined} />
                : <RegisterListing ticker={props.ticker} exchange={props.exchange} />}
        </div>

        <div data-label={TopShareholdersTabName}>
            <TopHolders ticker={props.ticker} exchange={props.exchange} />
        </div>

        <div data-label={HoldingsMovementTabName}>
            <HoldingsMovementReports ticker={props.ticker} exchange={props.exchange} combinedTicker={props.combinedTicker} />
        </div>

        <div data-label={SettingsTabName}>
            <ShareRegistrySettings ticker={props.ticker} exchange={props.exchange} combinedTicker={props.combinedTicker} />
        </div>

    </Tabs>
}

export default ShareRegistryPage;
